import { PIN_HEIGHT, PIN_WIDTH, PinInput, escapeHtml, cssUrl, requireImageUrl, PHOTO_GRADE } from "./types";

// venueCompare — a two-up "X vs Y" decision pin. Two full-bleed photos stacked
// (top = imageA, bottom = imageB), a big bold sans-serif label centred in each
// half, a white divider, and a white circular "vs" badge on the seam. No title
// or footer — the labels + photos carry it (matches the poster reference).
//   compare.imageA/labelA → top half
//   compare.imageB/labelB → bottom half

export function venueCompareTemplate(input: PinInput): string {
  // `brand` is the client's venue name. It's only present on client pins, so
  // it gates the bottom footer: client pins show the venue name; own-account
  // (Sauntera Weddings) compare pins pass no brand and stay footer-free.
  const { typography, compare, brand } = input;
  if (!compare?.imageA || !compare?.imageB) {
    throw new Error("venueCompare template requires compare { imageA, labelA, imageB, labelB }");
  }
  requireImageUrl({ ...input, imageUrl: compare.imageA }, "venueCompare(A)");

  // Variant B: a top question + an "or" badge (vs the clean "X vs Y" of A).
  const variantB = input.compareVariant === "b";
  const badge = variantB ? "or" : "vs";
  const question = (input.compareQuestion && input.compareQuestion.trim()) || "Where will you say ‘I do’?";

  return `<!doctype html>
<html><head>
<meta charset="utf-8" />
<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="${typography.googleFontsHref}" />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Bebas+Neue&display=swap" />
<style>
  *{margin:0;padding:0;box-sizing:border-box}
  html,body{width:${PIN_WIDTH}px;height:${PIN_HEIGHT}px;font-family:${typography.bodyFont};-webkit-font-smoothing:antialiased;background:#000}
  .pin{position:relative;width:100%;height:100%;overflow:hidden;color:#fff}
  .half{position:absolute;left:0;right:0;height:${PIN_HEIGHT / 2}px;background-size:cover;background-position:center;overflow:hidden;filter:${PHOTO_GRADE}}
  .top{top:0;background-image:url('${cssUrl(compare.imageA)}')}
  .bottom{top:${PIN_HEIGHT / 2}px;background-image:url('${cssUrl(compare.imageB)}')}
  /* A slight EVEN dim across each half (so bright/busy photos don't wash out the
     wide white labels) PLUS extra darkening behind each label's centre. */
  .top::after{content:"";position:absolute;inset:0;background:linear-gradient(0deg,rgba(0,0,0,.16),rgba(0,0,0,.16)),radial-gradient(ellipse 82% 42% at 50% 62%,rgba(0,0,0,.42) 0%,rgba(0,0,0,0) 72%)}
  .bottom::after{content:"";position:absolute;inset:0;background:linear-gradient(0deg,rgba(0,0,0,.16),rgba(0,0,0,.16)),radial-gradient(ellipse 82% 42% at 50% 38%,rgba(0,0,0,.42) 0%,rgba(0,0,0,0) 72%)}
  /* Big Bebas Neue labels (bold = synthetic, Bebas ships one weight), one
     centred in each half. */
  .label{position:absolute;left:40px;right:40px;z-index:3;text-align:center;line-height:.9;font-size:250px;
    font-family:'Bebas Neue',${typography.bodyFont},sans-serif;font-weight:500;text-transform:uppercase;letter-spacing:2px;color:#fff;
    text-shadow:0 4px 18px rgba(0,0,0,.55),0 2px 6px rgba(0,0,0,.45)}
  /* Centres symmetric about the seam (750), so both labels sit the same
     distance from the circle's top/bottom edges. */
  .labelA{top:420px;transform:translateY(-50%)}
  .labelB{top:1080px;transform:translateY(-50%)}
  /* White seam line. */
  .divider{position:absolute;top:${PIN_HEIGHT / 2}px;left:0;right:0;height:15px;background:#fff;transform:translateY(-50%);z-index:3}
  /* White circular "vs" badge on the seam — no drop shadow. */
  .vs{position:absolute;top:${PIN_HEIGHT / 2}px;left:50%;transform:translate(-50%,-50%);z-index:4;
    width:250px;height:250px;border-radius:50%;background:#fff;color:#2a2320;display:flex;align-items:center;justify-content:center;
    font-family:'Bebas Neue',${typography.bodyFont},sans-serif;font-weight:400;font-size:120px;line-height:1;
    -webkit-text-stroke:1px #fff}
  /* Bebas caps sit high in the line box — nudge down to optically centre. */
  .vsx{display:block;transform:translateY(0.04em)}
  /* Variant B: top question over a soft top scrim. */
  .topscrim{position:absolute;top:0;left:0;right:0;height:320px;z-index:2;background:linear-gradient(180deg,rgba(0,0,0,.6) 0%,rgba(0,0,0,0) 100%)}
  .question{position:absolute;top:104px;left:40px;right:40px;z-index:4;text-align:center;color:#fff;
    font-family:${typography.bodyFont};font-weight:600;font-size:44px;line-height:1.2;letter-spacing:4px;text-transform:uppercase;white-space:nowrap;
    text-shadow:0 3px 18px rgba(0,0,0,.6)}
  /* Client-only footer: venue name at the SAME level as venueScript's bottom
     tagline (bottom:96), same spaced-caps styling, over a soft scrim. */
  .footscrim{position:absolute;left:0;right:0;bottom:0;height:210px;z-index:2;background:linear-gradient(0deg,rgba(0,0,0,.55) 0%,rgba(0,0,0,0) 100%)}
  .footer{position:absolute;bottom:96px;left:56px;right:56px;z-index:3;text-align:center;color:#fff;
    font-size:30px;letter-spacing:7px;word-spacing:10px;text-transform:uppercase;font-weight:600;opacity:.96;
    text-shadow:0 2px 10px rgba(0,0,0,.6);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
</style></head>
<body><div class="pin">
  <div class="half top"></div>
  <div class="half bottom"></div>
  ${variantB ? `<div class="topscrim"></div><div class="question">${escapeHtml(question)}</div>` : ""}
  <div class="label labelA">${escapeHtml(compare.labelA)}</div>
  <div class="label labelB">${escapeHtml(compare.labelB)}</div>
  <div class="divider"></div>
  <div class="vs"><span class="vsx">${badge}</span></div>
  ${brand && brand.trim() ? `<div class="footscrim"></div><div class="footer">${escapeHtml(brand)}</div>` : ""}
</div></body></html>`;
}
